import { useRef } from "react";
import styled from "styled-components";

// cropperjs 기반 리액트 라이브러리
// 라이브러리 프롭스 옵션 문서 => https://github.com/fengyuanchen/cropperjs#options
import Cropper, { ReactCropperElement } from "react-cropper";
import "cropperjs/dist/cropper.css";

type CrossOrigin = "" | "anonymous" | "use-credentials" | undefined;

interface Props {
  sceneImg: string;
  onCrop: (image: string) => void;
  cropBoxData: Cropper.CropBoxData | null;
  onCropBoxData: (data: Cropper.CropBoxData) => void;
}

// ai 검색(크롭모드) 이미지 컴포넌트 - Cropper 라이브러리 사용
const Crop = ({ sceneImg, onCrop, cropBoxData, onCropBoxData }: Props) => {
  const cropperRef = useRef<ReactCropperElement>(null);

  // 크롭 이벤트
  const handleCrop = () => {
    const cropper = cropperRef.current?.cropper;

    if (cropper) {
      onCrop(cropper.getCroppedCanvas().toDataURL());
      onCropBoxData(cropper.getCropBoxData());
    }
  };

  const cropperProps = {
    ref: cropperRef,
    src: sceneImg,
    cropend: () => handleCrop(), // 크롭 드래그 끝날 때 이벤트 발생
    initialAspectRatio: 1,
    background: false,
    guides: false,
    center: false,
    movable: false,
    rotatable: false,
    scalable: false,
    zoomable: false,
    toggleDragModeOnDblclick: false,
    crossOrigin: "anonymous" as keyof CrossOrigin,
    checkOrientation: false,
    style: { width: "100%", height: "100%" },
    viewMode: 0 as Cropper.ViewMode,
    ready: () => {
      // 드래그 박스 데이터 상태 저장 => 핀생성 후 다시 이전 드래그 상태로 되돌아가기 위해서
      if (cropBoxData) {
        cropperRef.current?.cropper.setCropBoxData(cropBoxData);
      }
      // 자동 자르기 시작 시
      handleCrop();
    },
  };

  return (
    <div className="w-full h-full">
      <CustomCropper {...cropperProps} />
    </div>
  );
};

const CustomCropper = styled(Cropper)`
  .cropper-modal {
    opacity: 0.4;
  }

  .cropper-face {
    background: #d9d9d9;
  }

  .cropper-view-box {
    box-shadow: 0;
    outline: 0;
  }

  .cropper-point {
    opacity: 1;
    background-color: #fff;
    width: 12px;
    height: 3px;
  }

  .cropper-point.point-w {
    transform: rotate(90deg) translateY(4px);
  }

  .cropper-point.point-e {
    transform: rotate(90deg) translateY(-4px);
  }

  .cropper-point.point-nw,
  .cropper-point.point-ne,
  .cropper-point.point-se,
  .cropper-point.point-sw {
    width: 12px;
    height: 12px;
    background-color: transparent;
  }

  .cropper-point.point-nw {
    border-left: 3px solid #fff;
    border-top: 3px solid #fff;
  }

  .cropper-point.point-ne {
    border-right: 3px solid #fff;
    border-top: 3px solid #fff;
  }

  .cropper-point.point-se {
    border-right: 3px solid #fff;
    border-bottom: 3px solid #fff;
  }

  .cropper-point.point-sw {
    border-left: 3px solid #fff;
    border-bottom: 3px solid #fff;
  }

  .cropper-line {
    display: none;
  }
`;

export default Crop;
